import React, {
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import throttle from 'lodash/throttle';
import { FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import VideoPauseIcon from '@atlaskit/icon/core/video-pause';
import VideoPlayIcon from '@atlaskit/icon/core/video-play';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';
import {
	CardOrientationWrapper,
	useOrientationScrolling,
	useWidthAndSpacingCalculations,
} from '@atlassian/company-hub-common';

import { ARIA_CONTROLS_PREFIX, AUTOPLAY_SECONDS } from '../../../constants';
import { framesDimensions, FrameSizes, NavigationStyle } from '../../../types';

import { messages } from './messages';

type CarouselNavigationProps = {
	extensionLocalId: string;
	frameIds: string[];
	frames: ReactNode[];
	handleNavigationFrameUpdate: (index: number) => void;
	isAutoplayEnabled: boolean;
	isCarouselVisible: () => boolean;
	isHovered: boolean;
	navigationStyle: NavigationStyle;
	renderFrame: (frame: ReactNode, index: number) => ReactElement;
	selectedHeroFrameIndex: number;
	width?: number;
};

const NAVIGATION_PADDING_BREAKPOINT_WIDTH = 800;

const styles = cssMap({
	navigationContainer: {
		width: '100%',
		maxWidth: '100%',
	},
	navigationContainerWithPadding: {
		paddingInline: token('space.200'),
	},
	thumbnailNavigationContainer: {
		display: 'flex',
		position: 'relative',
		paddingLeft: token('space.0'),
		overflow: 'hidden',
		flex: '1 0 0',
	},
	left: {
		justifyContent: 'flex-start',
	},
	center: {
		justifyContent: 'center',
	},
	right: {
		justifyContent: 'flex-end',
	},
	progressContainer: {
		flex: '1 0 0',
	},
	progressPadding: {
		paddingLeft: token('space.300'),
	},
	iconBackgroundColor: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.full'),
	},
});

export const CarouselNavigation = ({
	extensionLocalId,
	frames,
	frameIds,
	selectedHeroFrameIndex,
	isHovered,
	isCarouselVisible,
	isAutoplayEnabled,
	navigationStyle,
	renderFrame,
	handleNavigationFrameUpdate,
	width = 0,
}: CarouselNavigationProps) => {
	const isThumbnailNavigation = navigationStyle === NavigationStyle.THUMBNAILS;
	const [isAutoPlaying, setIsAutoPlaying] = useState(isAutoplayEnabled);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const frameSize = framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL_V2];

	const { calculations, setCurrentCardsGridWidth, setCurrentCardWidth } =
		useWidthAndSpacingCalculations({
			numberOfCards: frames.length,
			cardDimensions: framesDimensions[FrameSizes.NAVIGATION_THUMBNAIL_V2],
		});

	const { cardWidthOnFullRow, isFirstRowFull, gaps, maxNumberOfCardsPerRow, isFirstRowOverflown } =
		calculations;

	const throttledSetWidth = throttle(setCurrentCardsGridWidth, 200);
	const spaceBetweenFrames = gaps.spaceBetweenCards;

	const { scrollToPrevious, scrollToNext, scrollToIndex, requestScrollIndex, resetScrollIndex } =
		useOrientationScrolling({
			maxNumberOfItemsPerRow: maxNumberOfCardsPerRow,
			numberOfItems: frames?.length,
			isRow: true,
		});

	const handleNextClick = useCallback(() => {
		if (selectedHeroFrameIndex < frames.length - 1) {
			handleNavigationFrameUpdate(selectedHeroFrameIndex + 1);
			scrollToNext();
		} else {
			requestScrollIndex(0);
			handleNavigationFrameUpdate(0);
		}
	}, [
		selectedHeroFrameIndex,
		frames.length,
		handleNavigationFrameUpdate,
		scrollToNext,
		requestScrollIndex,
	]);

	const handlePreviousClick = useCallback(() => {
		if (selectedHeroFrameIndex > 0) {
			scrollToPrevious();
			handleNavigationFrameUpdate(selectedHeroFrameIndex - 1);
		} else {
			const lastFrameIndex = frames.length - 1;
			requestScrollIndex(lastFrameIndex);
			handleNavigationFrameUpdate(lastFrameIndex);
		}
	}, [
		selectedHeroFrameIndex,
		frames.length,
		handleNavigationFrameUpdate,
		scrollToPrevious,
		requestScrollIndex,
	]);

	const handlePausePlayClick = () => {
		setIsAutoPlaying((prev) => !prev);
	};

	const handleSelect = ({ index: selectedIndex }: { index: number }): void => {
		handleNavigationFrameUpdate(selectedIndex);
	};

	// This useEffect is used to Auto-scroll every X seconds
	useEffect(() => {
		if (isAutoPlaying && !isHovered) {
			intervalRef.current = setInterval(() => {
				if (isCarouselVisible()) {
					handleNextClick();
				}
			}, AUTOPLAY_SECONDS * 1000);
		}
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [isAutoPlaying, isCarouselVisible, isHovered, selectedHeroFrameIndex, handleNextClick]);

	return (
		<Inline
			xcss={cx(
				styles.navigationContainer,
				width > NAVIGATION_PADDING_BREAKPOINT_WIDTH && styles.navigationContainerWithPadding,
			)}
			space="space.100"
			alignBlock="center"
			testId="carousel-navigation"
		>
			{isAutoplayEnabled ? (
				<Inline
					xcss={cx(styles.iconBackgroundColor)}
					space="space.050"
					alignInline="start"
					testId="carousel-navigation-autoplay"
				>
					{isAutoPlaying ? (
						<IconButton
							shape="circle"
							icon={VideoPauseIcon}
							label={<FormattedMessage {...messages.pause} />}
							onClick={handlePausePlayClick}
						/>
					) : (
						<IconButton
							shape="circle"
							icon={VideoPlayIcon}
							label={<FormattedMessage {...messages.play} />}
							onClick={handlePausePlayClick}
						/>
					)}
				</Inline>
			) : (
				<Box xcss={cx(styles.iconBackgroundColor)}>
					<IconButton
						shape="circle"
						icon={ArrowLeftIcon}
						label={<FormattedMessage {...messages.previous} />}
						onClick={handlePreviousClick}
					/>
				</Box>
			)}
			{isThumbnailNavigation ? (
				<Box
					xcss={cx(
						styles.thumbnailNavigationContainer,
						isFirstRowOverflown ? styles.left : styles.center,
					)}
					style={{
						gap: `${spaceBetweenFrames}px`,
					}}
				>
					{frames.map((frame, index) => (
						<CardOrientationWrapper
							cardId={frameIds[index]}
							key={index}
							isFirstRowFull={isFirstRowFull}
							cardWidthOnFullRow={cardWidthOnFullRow}
							setWidth={index === 0 ? setCurrentCardWidth : undefined}
							scrollToCard={index === scrollToIndex}
							clearScrollToCard={resetScrollIndex}
							testId={`carousel-navigation-content-frames-row-frame-${index}`}
							index={index}
							requestScrollIndex={requestScrollIndex}
							extensionLocalId={extensionLocalId}
							hasInlinePadding
							itemDimensions={frameSize}
						>
							{renderFrame(frame, index)}
						</CardOrientationWrapper>
					))}
					<WidthObserver setWidth={throttledSetWidth} offscreen />
				</Box>
			) : (
				<Inline
					alignInline="center"
					xcss={cx(styles.progressContainer, isAutoplayEnabled && styles.progressPadding)}
				>
					<ProgressIndicator
						values={frameIds}
						selectedIndex={selectedHeroFrameIndex}
						onSelect={handleSelect}
						ariaControls={`${ARIA_CONTROLS_PREFIX}${extensionLocalId}`}
					/>
				</Inline>
			)}
			<Inline
				space="space.050"
				alignInline="end"
				testId="carousel-navigation-content-cards-row-navigation"
			>
				{isAutoplayEnabled && (
					<Inline xcss={cx(styles.iconBackgroundColor)}>
						<IconButton
							shape="circle"
							icon={ArrowLeftIcon}
							label={<FormattedMessage {...messages.previous} />}
							onClick={handlePreviousClick}
						/>
					</Inline>
				)}
				<Inline xcss={cx(styles.iconBackgroundColor)}>
					<IconButton
						shape="circle"
						icon={ArrowRightIcon}
						label={<FormattedMessage {...messages.next} />}
						onClick={handleNextClick}
					/>
				</Inline>
			</Inline>
		</Inline>
	);
};
